import React, { useRef, useState } from 'react';

import { ControlledMenu, MenuItem, useHover } from '@szhsin/react-menu';
import { useTranslation } from 'react-i18next';
import { CiGlobe } from 'react-icons/ci';
import { MdChevronRight } from 'react-icons/md';
import { SingleEntry } from '../../graphql-fragments/navigationMenu';
import { clsx } from '../../utils/utils';
import MenuEntry from './MenuEntry';
import * as styles from './MultilanguageSelector.module.scss';

export interface MultilanguageSelectorProps {
  title: string;
  languages: Array<SingleEntry>;
  onClick?: React.MouseEventHandler<HTMLElement>;
  className?: string;
}

const MultilanguageSelector = ({
  title,
  languages,
  onClick,
  className,
}: MultilanguageSelectorProps): React.ReactElement => {
  const ref = useRef(null);
  const [isEntryOpen, setIsEntryOpen] = useState(false);
  const { anchorProps, hoverProps } = useHover(isEntryOpen, setIsEntryOpen);

  const { i18n } = useTranslation();

  return (
    <div className={clsx(className, styles.container)}>
      <button
        className={clsx(
          styles.subMenuWrapperDesktop,
          isEntryOpen && styles.nestedEntryOpen,
          styles.menuLink,
        )}
        ref={ref}
        aria-expanded={isEntryOpen}
        aria-disabled="false"
        aria-controls={'panel_menu'}
        tabIndex={0}
        role="button"
        {...anchorProps}
      >
        <CiGlobe className={styles.multilingueIcon} />

        {title}
        <MdChevronRight className={styles.icon} />
      </button>

      <ControlledMenu
        {...hoverProps}
        menuClassName={styles.subMenuDesktop}
        onClose={() => {
          setIsEntryOpen(false);
        }}
        state={isEntryOpen ? 'open' : 'closed'}
        anchorRef={ref}
        gap={8}
      >
        {languages.map((language, i) => (
          <MenuItem key={i} className={styles.nestedItem}>
            <MenuEntry
              entry={language}
              key={i}
              onClick={onClick}
              className={clsx(styles.link, i18n.language === language.title && styles.active)}
              noLocalize
            />
          </MenuItem>
        ))}
      </ControlledMenu>
    </div>
  );
};

export default MultilanguageSelector;
